// src/hooks/useReviewHeartbeat.js
//
// One ping per 30-second slot while the review page is VISIBLE, batched and
// posted every two minutes.
//
// ── WHAT THIS IS FOR ─────────────────────────────────────────────────────────
// From verdict timestamps alone a long gap is both "a hard record" and "time
// away" — the same number — so any break threshold guesses, and it guesses
// upward: a Tier C record that genuinely took 25 minutes gets classified as a
// break and its time leaves the denominator. The heartbeat separates work from
// break directly, which is the entire reason it exists.
//
// It does NOT measure dwell. 30s slots cannot resolve a 4s record. Dwell comes
// from verdict-to-verdict spacing, and the heartbeat is what says which of
// those intervals were work. Two mechanisms, two questions.
//
// ── visibilityState, AND ITS LIMIT ───────────────────────────────────────────
// A hidden tab is not eyes-on, and that is exactly the distinction a verdict
// gap cannot make. But a VISIBLE tab on an unwatched second monitor still
// pings, so this is an upper bound. Tightening it would need input-activity
// detection — mouse and key sampling — which is materially more invasive for a
// marginal gain, and the surface says "upper bound" rather than pretending
// otherwise.

import { useEffect, useRef } from "react";
import { fetchWithAuth } from "../api/fetchWithAuth";

const SLOT_MS = 30_000;
const FLUSH_MS = 120_000;
// One hour of backlog. Matches the server's per-batch cap; beyond it the oldest
// slots are dropped rather than growing without bound after a long offline
// stretch, and dropping the OLDEST is right because the recent ones are the
// ones still adjacent to live work.
const MAX_PENDING = 240;

/** The 30s slot a moment falls in, as an ISO string. Rounding is what makes retries idempotent. */
export function slotFor(ms = Date.now()) {
  return new Date(Math.floor(ms / SLOT_MS) * SLOT_MS).toISOString();
}

/**
 * @param {boolean} active   only run on the review surface, for the right org
 * @param {() => {record_id: string|null, tier: string|null}} getContext
 *   read at ping time, not at mount: the open record changes every few seconds
 *   and a captured value would attribute every slot to the first record.
 */
export function useReviewHeartbeat(active, getContext) {
  const pending = useRef([]);
  const lastSlot = useRef(null);
  const ctx = useRef(getContext);
  ctx.current = getContext;

  useEffect(() => {
    if (!active) return undefined;

    const capture = () => {
      // The single check that makes this a measure of attention rather than of
      // an open tab.
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      const at = slotFor();
      // One row per slot. A 30s timer can fire twice inside one slot after a
      // tab throttle or a clock adjustment, and the server would dedupe it, but
      // sending it at all wastes the batch.
      if (at === lastSlot.current) return;
      lastSlot.current = at;
      const { record_id = null, tier = null } = ctx.current?.() ?? {};
      pending.current.push({ at, record_id, tier });
      if (pending.current.length > MAX_PENDING) {
        pending.current = pending.current.slice(-MAX_PENDING);
      }
    };

    const flush = async ({ beacon = false } = {}) => {
      if (!pending.current.length) return;
      const slots = pending.current;
      // Cleared BEFORE the request. A failed post loses at most one batch of
      // timing data, where holding them for retry risks an unbounded buffer and
      // a thundering re-post after an outage. The data is cheap; the buffer is
      // not.
      pending.current = [];
      const body = JSON.stringify({ slots });
      try {
        if (beacon && navigator?.sendBeacon) {
          // On pagehide the tab may be killed before fetch resolves. Beacon is
          // fire-and-forget and survives it — but it cannot carry the auth
          // header, so it only fires where cookies suffice; the catch below
          // covers the rest.
          navigator.sendBeacon("/api/v1/wellfinder/activity", new Blob([body], { type: "application/json" }));
          return;
        }
        await fetchWithAuth("/api/v1/wellfinder/activity", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body,
        });
      } catch {
        /* best effort — see above */
      }
    };

    capture();
    const tick = setInterval(capture, SLOT_MS);
    const push = setInterval(flush, FLUSH_MS);

    // Capture on the way back so the first slot after a tab switch is not lost,
    // and flush on the way out so a closed tab does not take two minutes of
    // measurement with it.
    const onVisibility = () => {
      if (document.visibilityState === "visible") capture();
      else flush();
    };
    const onHide = () => flush({ beacon: true });
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onHide);

    return () => {
      clearInterval(tick);
      clearInterval(push);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onHide);
      flush();
    };
  }, [active]);
}
